import React, { useState, useEffect } from 'react';
import { getAllBalanceMovements, getUsers, getSubadmins } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { buildUserOptions, ROLE_LABEL } from '../../lib/userOptions';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Wallet, Search, ArrowUpRight, ArrowDownRight, Filter, X, Calendar } from 'lucide-react';

export default function BalanceMovements() {
  const { user: me } = useAuth();
  const [movements, setMovements] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [movRes, usersRes, subsRes] = await Promise.all([
          getAllBalanceMovements(),
          getUsers(),
          me?.role === 'admin' ? getSubadmins() : Promise.resolve({ data: [] }),
        ]);
        setMovements(movRes.data);
        setFiltered(movRes.data);
        setUsers(buildUserOptions([me, ...subsRes.data, ...usersRes.data], movRes.data));
      } catch (err) {
        console.error('Error fetching movements:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let result = [...movements];

    // Filter by search text
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (m) =>
          m.username.toLowerCase().includes(lower) ||
          m.reason.toLowerCase().includes(lower)
      );
    }

    // Filter by user
    if (selectedUser && selectedUser !== 'all') {
      result = result.filter((m) => m.username === selectedUser);
    }

    // Filter by type
    if (selectedType && selectedType !== 'all') {
      result = result.filter((m) => m.type === selectedType);
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((m) => new Date(m.created_at) >= fromDate);
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((m) => new Date(m.created_at) <= toDate);
    }

    setFiltered(result);
  }, [search, selectedUser, selectedType, dateFrom, dateTo, movements]);

  const clearFilters = () => {
    setSearch('');
    setSelectedUser('all');
    setSelectedType('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = search || selectedUser !== 'all' || selectedType !== 'all' || dateFrom || dateTo;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="balance-movements">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Movimientos de Saldo</h1>
          <p className="text-muted-foreground mt-1">
            Historial de cargas y consumos
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border/40">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-primary" />
            <span className="font-medium">Filtros</span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="ml-auto text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Usuario, razón..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="search-movements"
                />
              </div>
            </div>

            {/* User filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Usuario</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-secondary/50" data-testid="filter-user">
                  <SelectValue placeholder="Todos los usuarios" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.username} value={u.username} data-testid={`filter-user-option-${u.username}`}>
                      {u.username}
                      {u.role && <span className="ml-2 text-xs text-muted-foreground">({ROLE_LABEL[u.role]})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type filter */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Tipo</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-secondary/50" data-testid="filter-type">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="credit">Crédito (Cargas)</SelectItem>
                  <SelectItem value="debit">Débito (Consumos)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date from */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Desde</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="filter-date-from"
                />
              </div>
            </div>

            {/* Date to */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Hasta</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="pl-9 bg-secondary/50"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground mt-3">
              Mostrando {filtered.length} de {movements.length} movimientos
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border/40">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay movimientos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead>Tipo</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Razón</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => (
                    <TableRow
                      key={m.id}
                      className="border-border/30 hover:bg-secondary/30"
                    >
                      <TableCell>
                        {m.type === 'credit' ? (
                          <div className="flex items-center gap-2 text-accent">
                            <ArrowUpRight className="w-4 h-4" />
                            <span className="font-medium">Crédito</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-destructive">
                            <ArrowDownRight className="w-4 h-4" />
                            <span className="font-medium">Débito</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{m.username}</TableCell>
                      <TableCell
                        className={`font-mono font-bold ${
                          m.type === 'credit' ? 'text-accent' : 'text-destructive'
                        }`}
                      >
                        {m.type === 'credit' ? '+' : '-'}
                        {formatCurrency(m.amount)}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {m.reason}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {m.admin_username}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(m.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
